/** @format */

import { Component, OnDestroy } from "@angular/core";
import { Company } from "src/app/models/company.model";
import { Plant } from "src/app/models/plant.model";
import { Unit } from "src/app/models/unit.model";
import { Logsheet } from "src/app/models/logsheet.model";
import { CompanyService } from "src/app/services/company/company.service";
import { PlantService } from "src/app/services/plant/plant.service";
import { UnitService } from "src/app/services/unit/unit.service";
import { LogsheetService } from "src/app/services/logsheet/logsheet.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { Roles, isAdmin, isSuperAdmin } from "src/app/auth/roles";
import { RefreshStateService } from "src/app/services/refresh-state/refresh-state.service";
import { Subscription } from "rxjs";

interface ReportsRefreshState {
  companyId: number;
  plantId: number;
  unitId: number;
  formId: number;
  reportId: number;
  formatId: number;
  startDate: string;
  endDate: string;
}

@Component({
  standalone: false,
  selector: "app-reports",
  templateUrl: "./reports.component.html",
  styleUrls: ["./reports.component.css"],
})
export class ReportsComponent implements OnDestroy {
  private readonly menuResetSubscription = new Subscription();
  currentRole: any = "";
  roles = Roles;
  assignedPlants: any = [];
  companies: Company[] = []; // company is Account Holder eg. Excelsior HS
  plants: Plant[] = []; // plant is Client Name eg. sports Department
  units: Unit[] = []; // unit is Branch eg. swim Team
  forms: Logsheet[] = []; // Swim Team Training
  reportTypes: any[] = [
    { reportId: 1, reportName: "Submission Count Overview", isDeleted: false },
    { reportId: 2, reportName: "Form submission Extract", isDeleted: false },
  ];
  reportFormats: any[] = [{ formatId: 1, formatName: "CSV", isDeleted: false }];
  resetPlant = false;
  resetUnit = false;

  selectedValues = {
    company: {
      companyId: 0,
      companyName: "",
      isDeleted: false,
    },
    plant: {
      plantId: 0,
      plantName: "",
      isDeleted: false,
    },
    unit: {
      unitId: 0,
      unitName: "",
      isDeleted: false,
    },
  };

  selectedReport = {
    reportId: 0,
    reportName: "",
    isDeleted: false,
  };

  selectedForm = {
    logSheetTemplateId: 0,
    logSheetName: "",
    isDeleted: false,
  };

  selectedFormat = {
    formatId: 0,
    formatName: "",
    isDeleted: false,
  };

  startDate: string = "";
  endDate: string = "";
  isGenerating: boolean = false;
  reportReady: boolean = false;
  reportData: any = null;
  dateError: string = "";

  errorMessage = {
    success: true,
    message: "",
  };

  constructor(
    private companyService: CompanyService,
    private plantService: PlantService,
    private unitService: UnitService,
    private logsheetService: LogsheetService,
    private errorService: ErrorHandlerService,
    private refreshState: RefreshStateService,
  ) {}

  ngOnInit(): void {
    this.menuResetSubscription.add(
      this.refreshState.areaReset$.subscribe(area => {
        if (area === "reports") this.resetToMenuHead();
      }),
    );
    const saved = this.refreshState.load<ReportsRefreshState>("reports");
    this.selectedReport = this.reportTypes.find(report => report.reportId === saved?.reportId) || this.selectedReport;
    this.selectedFormat = this.reportFormats.find(format => format.formatId === saved?.formatId) || this.selectedFormat;
    this.startDate = saved?.startDate || "";
    this.endDate = saved?.endDate || "";
    this.currentRole = localStorage.getItem("role");

    try {
      const storedAssignment = localStorage.getItem("assignedPlants");
      if (storedAssignment) {
        this.assignedPlants = JSON.parse(storedAssignment);
      }
    } catch (error) {
      console.error("Error parsing JSON data from local storage:", error);
    }

    if (isAdmin(this.currentRole)) {
      this.assignedPlants.map((assignment: Plant) =>
        this.plants.push(assignment),
      );

      const restoredPlant = this.plants.find(plant => plant.plantId === saved?.plantId);
      const initialPlant = restoredPlant || (this.plants.length === 1 ? this.plants[0] : null);
      if (initialPlant) {
        this.selectedValues.plant = initialPlant;
        this.retrieveUnitsByPlantId(initialPlant, true, saved?.unitId, saved?.formId);
        // Load the company (Account Holder) information for this plant
        this.loadCompanyForPlant(initialPlant.plantId);
      }
    } else if (isSuperAdmin(this.currentRole)) {
      this.companyService.retrieveCompanies().subscribe({
        next: (companies) => {
          this.companies = companies;
          const restoredCompany = companies.find(company => company.companyId === saved?.companyId);
          if (restoredCompany) {
            this.retrievePlantsByCompanyId(restoredCompany, true, saved?.plantId, saved?.unitId, saved?.formId);
          }
        },
        error: (response) => {
          this.errorMessage = this.errorService.handleRetrievalErrors(response);
        },
      });
    }
  }

  retrievePlantsByCompanyId(selected: any, initial?: boolean, plantId?: number, unitId?: number, formId?: number) {
    this.selectedValues.company = selected;

    if (!initial) {
      if (this.selectedValues.plant.plantId > 0 || this.plants.length > 0) {
        this.resetPlant = true;
        this.resetUnit = true;
        this.selectedValues.plant = {
          plantId: 0,
          plantName: "",
          isDeleted: false,
        };
        this.selectedValues.unit = {
          unitId: 0,
          unitName: "",
          isDeleted: false,
        };
        this.plants = [];
        this.units = [];
      }
    }

    this.plantService
      .retrieveCompanyPlants(this.selectedValues.company.companyId.toString())
      .subscribe({
        next: (response) => {
          this.plants = response;
          const restoredPlant = this.plants.find(plant => plant.plantId === plantId);
          if (restoredPlant) this.retrieveUnitsByPlantId(restoredPlant, true, unitId, formId);
          setTimeout(() => {
            this.resetPlant = false;
          }, 0);
          this.persistRefreshState();
        },
        error: (err) => {
          this.errorMessage = this.errorService.handleRetrievalErrors(err);
          setTimeout(() => {
            this.resetPlant = false;
          }, 0);
        },
      });
  }

  retrieveUnitsByPlantId(selected: any, initial?: boolean, unitId?: number, formId?: number) {
    this.selectedValues.plant = selected;

    // Load the company information for this plant (for Admin users)
    if (isAdmin(this.currentRole) && selected.plantId) {
      this.loadCompanyForPlant(selected.plantId);
    }

    if (!initial) {
      if (this.selectedValues.unit.unitId > 0 || this.units.length > 0) {
        this.resetUnit = true;
        this.selectedValues.unit = {
          unitId: 0,
          unitName: "",
          isDeleted: false,
        };
        this.units = [];
      }
    }

    this.unitService
      .retrieveUnitPlant(this.selectedValues.plant.plantId.toString())
      .subscribe({
        next: (response) => {
          this.units = response;
          const restoredUnit = this.units.find(unit => unit.unitId === unitId);
          if (restoredUnit) {
            this.selectedValues.unit = {
              unitId: restoredUnit.unitId,
              unitName: restoredUnit.unitName,
              isDeleted: restoredUnit.isDeleted || false,
            };
            this.loadForms(formId);
          }
          setTimeout(() => {
            this.resetUnit = false;
          }, 0);
          this.persistRefreshState();
        },
        error: (err) => {
          this.errorMessage = this.errorService.handleRetrievalErrors(err);
          setTimeout(() => {
            this.resetUnit = false;
          }, 0);
        },
      });
  }

  onUnitSelected(selected: any) {
    if (this.selectedValues.unit.unitId !== selected.unitId) {
      this.selectedValues.unit = selected;
      this.loadForms();
      this.resetReportState();
      this.persistRefreshState();
    }
  }

  loadForms(formId?: number) {
    if (this.selectedValues.unit.unitId > 0) {
      this.logsheetService
        .retrieveLogsheetByUnit(this.selectedValues.unit.unitId)
        .subscribe({
          next: (response) => {
            this.forms = response.sort((a, b) =>
              a.logSheetName.localeCompare(b.logSheetName),
            );
            const restoredForm = this.forms.find(form => form.logSheetTemplateId === formId);
            if (restoredForm) {
              this.selectedForm = {
                logSheetTemplateId: restoredForm.logSheetTemplateId,
                logSheetName: restoredForm.logSheetName,
                isDeleted: restoredForm.isDeleted || false,
              };
            }
            this.persistRefreshState();
          },
          error: (err) => {
            this.errorMessage = this.errorService.handleRetrievalErrors(err);
          },
        });
    }
  }

  onReportSelected(selected: any) {
    if (this.selectedReport.reportId !== selected.reportId) {
      this.selectedReport = selected;
      this.resetReportState();
    } else {
      this.selectedReport = selected;
    }
    this.persistRefreshState();
  }

  onFormSelected(selected: any) {
    if (this.selectedForm.logSheetTemplateId !== selected.logSheetTemplateId) {
      this.selectedForm = selected;
      this.resetReportState();
    } else {
      this.selectedForm = selected;
    }
    this.persistRefreshState();
  }

  onFormatSelected(selected: any) {
    if (this.selectedFormat.formatId !== selected.formatId) {
      this.selectedFormat = selected;
      this.resetReportState();
    } else {
      this.selectedFormat = selected;
    }
    this.persistRefreshState();
  }

  resetReportState() {
    this.reportReady = false;
    this.reportData = null;
  }

  validateDates() {
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      if (end < start) {
        this.dateError = "End date cannot be less than start date";
        return false;
      }
    }
    this.dateError = "";
    return true;
  }

  onDateChange(): void {
    this.validateDates();
    this.resetReportState();
    this.persistRefreshState();
  }

  isFormValid(): boolean {
    return (
      this.selectedReport.reportId > 0 &&
      this.selectedForm.logSheetTemplateId > 0 &&
      this.startDate !== "" &&
      this.endDate !== "" &&
      this.validateDates() &&
      this.selectedFormat.formatId > 0
    );
  }

  generateReport() {
    if (this.isFormValid()) {
      this.isGenerating = true;
      this.reportReady = false;

      const item = {
        id: this.selectedForm.logSheetTemplateId,
        pageNumber: 1,
        pageSize: 1000,
        startDate: this.startDate,
        endDate: this.endDate,
      };

      this.logsheetService.retrieveLogSheetByRelativeId(item).subscribe({
        next: (response) => {
          const generationTime = new Date();
          this.reportData = {
            data: response,
            report: this.selectedReport,
            form: this.selectedForm,
            startDate: this.startDate,
            endDate: this.endDate,
            format: this.selectedFormat,
            unit: this.selectedValues.unit,
            plant: this.selectedValues.plant,
            company: this.selectedValues.company,
            generatedAt:
              this.formatDateValue(
                `${generationTime.getFullYear()}-${String(generationTime.getMonth() + 1).padStart(2, "0")}-${String(generationTime.getDate()).padStart(2, "0")}`,
              ) +
              " at " +
              generationTime.toLocaleString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              }),
          };

          this.isGenerating = false;
          this.reportReady = true;
        },
        error: (response) => {
          console.error("Error fetching report data:", response);
          this.errorMessage = this.errorService.handleRetrievalErrors(response);
          this.isGenerating = false;
          this.reportReady = false;
        },
      });
    }
  }

  downloadReport() {
    if (this.reportReady && this.reportData) {
      // TODO: Implement actual CSV download logic
      const csvContent = this.generateCSVContent();
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${this.selectedForm.logSheetName}_${this.startDate}_to_${this.endDate}.csv`,
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  generateCSVContent(): string {
    const header = [
      `Report Name,${this.selectedReport.reportName}`,
      `Account Holder,${this.selectedValues.company.companyName || "N/A"}`,
      `Client Name,${this.selectedValues.plant.plantName}`,
      `Branch Name,${this.selectedValues.unit.unitName}`,
      `Form Name,${this.selectedForm.logSheetName}`,
      `Report Generation Date and Time,${this.reportData.generatedAt}`,
      `Date Range,${this.formatDateValue(this.startDate)} to ${this.formatDateValue(this.endDate)}`,
      "",
      "Report Results:",
      "",
    ].join("\n");

    const rawData = this.reportData?.data || [];

    // Filter out unfilled submissions (only exclude if no data point values)
    const data = rawData.filter((record: any) => {
      const templateData = record.logSheetTemplated || record.LogSheetTemplated;
      const dataPoints = templateData?.dataPointDetails || [];
      return dataPoints.some((dp: any) =>
        dp.dataPointValue && dp.dataPointValue.length > 0
      );
    });

    if (data.length === 0) {
      return `${header}\nNo completed submissions available for the selected date range.`;
    }

    const firstRecord = data[0];

    // Try both property name variations (camelCase and PascalCase)
    const templateData =
      firstRecord.logSheetTemplated || firstRecord.LogSheetTemplated;

    // Create a copy before sorting to avoid mutating the original array
    const dataPointDetails = templateData?.dataPointDetails
      ? [...templateData.dataPointDetails].sort(
          (a: any, b: any) => a.orderOnSheet - b.orderOnSheet,
        )
      : [];

    // Route based on selected report type
    if (this.selectedReport.reportId === 2) {
      return this.formSubmissionExtract(header, data, dataPointDetails);
    }

    // Check if we have list-type data points (both single and multiple selection)
    const hasListDataPoints = dataPointDetails.some(
      (dp: any) =>
        (dp.dataType === "List (Single Selection)" ||
          dp.dataType === "List (Multiple Selection)" ||
          dp.dataType === "Dropdown List") &&
        dp.listOptions,
    );

    if (hasListDataPoints) {
      return this.generateAttendanceSummary(header, data, dataPointDetails);
    } else {
      return this.generateStandardReport(header, data, dataPointDetails);
    }
  }

  generateStandardReport(
    header: string,
    data: any[],
    dataPointDetails: any[],
  ): string {
    const dataPointHeaders = dataPointDetails.map(
      (dp: any) => dp.dataPointName,
    );

    const columnHeaders = [
      "Submission Date/Time",
      "Status",
      "User",
      "Version",
      ...dataPointHeaders,
    ].join(",");

    const dataRows = data
      .map((record: any) => {
        const executionTime = record.executionTime
          ? new Date(record.executionTime).toLocaleString()
          : "N/A";
        const status = record.status || "N/A";
        const user =
          record.lastModifiedByUserEmail || record.currentUser || "N/A";
        const version = record.versionNumber || "N/A";

        const dataPointValues = dataPointDetails.map((dp: any) => {
          const matchingDp = record.logSheetTemplated?.dataPointDetails?.find(
            (rdp: any) => rdp.dataPointId === dp.dataPointId,
          );
          const value = matchingDp?.dataPointValue?.[0]?.value || "";
          return this.escapeCsvValue(value);
        });

        return [
          this.escapeCsvValue(executionTime),
          this.escapeCsvValue(status),
          this.escapeCsvValue(user),
          this.escapeCsvValue(version),
          ...dataPointValues,
        ].join(",");
      })
      .join("\n");

    return `${header}\n${columnHeaders}\n${dataRows}`;
  }

  generateAttendanceSummary(
    header: string,
    data: any[],
    dataPointDetails: any[],
  ): string {
    // Find ALL list-type data points (both single and multiple selection)
    const personDataPoints = dataPointDetails.filter(
      (dp: any) =>
        (dp.dataType === "List (Single Selection)" ||
          dp.dataType === "List (Multiple Selection)" ||
          dp.dataType === "Dropdown List") &&
        dp.listOptions,
    );

    if (personDataPoints.length === 0) {
      return this.generateStandardReport(header, data, dataPointDetails);
    }

    // Get list options from the first person data point (e.g., "present,late,absent")
    const listOptions = personDataPoints[0].listOptions
      .split(",")
      .map((opt: string) => opt.trim());

    // Get schedule information from the form template
    const firstRecord = data[0];

    // Try both property name variations
    const templateData =
      firstRecord.logSheetTemplated || firstRecord.LogSheetTemplated;

    const scheduleStart = templateData?.scheduleStart;
    const scheduleInfo = scheduleStart
      ? `\nSchedule Period: ${new Date(scheduleStart).toLocaleDateString()} to ${this.endDate ? new Date(this.endDate).toLocaleDateString() : "Present"}`
      : "";

    // Initialize attendance counts for ALL people
    const attendanceCounts: any = {};
    personDataPoints.forEach((dp: any) => {
      attendanceCounts[dp.dataPointName] = {};
      listOptions.forEach((option: string) => {
        attendanceCounts[dp.dataPointName][option] = 0;
      });
    });

    // Count attendance across all submissions
    data.forEach((record: any, index: number) => {
      const recordTemplateData =
        record.logSheetTemplated || record.LogSheetTemplated;
      const recordDataPoints = recordTemplateData?.dataPointDetails || [];

      // For each person data point
      personDataPoints.forEach((personDp: any) => {
        const personRecordDp = recordDataPoints.find(
          (rdp: any) => rdp.dataPointId === personDp.dataPointId,
        );

        // Get the attendance value for this person (present, late, absent)
        const attendanceValue = personRecordDp?.dataPointValue?.[0]?.value;

        // If this person has an attendance value and it's one of our options, increment the count
        if (attendanceValue && listOptions.includes(attendanceValue)) {
          attendanceCounts[personDp.dataPointName][attendanceValue]++;
        }
      });
    });

    // Build CSV table header with schedule info
    const headerWithSchedule = header + scheduleInfo;

    // Check if there are exactly 2 list data points - one for names, one for status
    if (personDataPoints.length === 2) {
      // Determine which data point is for names and which is for status
      // Use keyword detection in the data point name
      const [dp1, dp2] = personDataPoints;
      const dp1Options = dp1.listOptions
        .split(",")
        .map((opt: string) => opt.trim());
      const dp2Options = dp2.listOptions
        .split(",")
        .map((opt: string) => opt.trim());

      let namesDp: any;
      let statusDp: any;
      let nameOptions: string[];
      let statusOptions: string[];

      // Keywords that indicate a "names" field (people/items being tracked)
      const nameKeywords = [
        "name",
        "person",
        "athlete",
        "student",
        "employee",
        "member",
        "teacher",
        "player",
        "participant",
      ];
      // Keywords that indicate a "status" field (the attribute being measured)
      const statusKeywords = [
        "status",
        "attendance",
        "present",
        "absent",
        "late",
      ];

      const dp1NameLower = dp1.dataPointName.toLowerCase();
      const dp2NameLower = dp2.dataPointName.toLowerCase();

      const dp1IsNames = nameKeywords.some((kw) => dp1NameLower.includes(kw));
      const dp2IsNames = nameKeywords.some((kw) => dp2NameLower.includes(kw));
      const dp1IsStatus = statusKeywords.some((kw) =>
        dp1NameLower.includes(kw),
      );
      const dp2IsStatus = statusKeywords.some((kw) =>
        dp2NameLower.includes(kw),
      );

      // Determine based on keywords, fallback to orderOnSheet
      if (dp1IsNames && !dp2IsNames) {
        namesDp = dp1;
        statusDp = dp2;
        nameOptions = dp1Options;
        statusOptions = dp2Options;
      } else if (dp2IsNames && !dp1IsNames) {
        namesDp = dp2;
        statusDp = dp1;
        nameOptions = dp2Options;
        statusOptions = dp1Options;
      } else if (dp1IsStatus && !dp2IsStatus) {
        // dp1 is status, so dp2 is names
        namesDp = dp2;
        statusDp = dp1;
        nameOptions = dp2Options;
        statusOptions = dp1Options;
      } else if (dp2IsStatus && !dp1IsStatus) {
        // dp2 is status, so dp1 is names
        namesDp = dp1;
        statusDp = dp2;
        nameOptions = dp1Options;
        statusOptions = dp2Options;
      } else {
        // Fallback: use orderOnSheet (first field on form is names)
        if (dp1.orderOnSheet <= dp2.orderOnSheet) {
          namesDp = dp1;
          statusDp = dp2;
          nameOptions = dp1Options;
          statusOptions = dp2Options;
        } else {
          namesDp = dp2;
          statusDp = dp1;
          nameOptions = dp2Options;
          statusOptions = dp1Options;
        }
      }

      // Initialize counts: names as rows, statuses as columns
      const crossCounts: { [name: string]: { [status: string]: number } } = {};
      nameOptions.forEach((name: string) => {
        crossCounts[name] = {};
        statusOptions.forEach((status: string) => {
          crossCounts[name][status] = 0;
        });
      });

      // Count across all submissions
      data.forEach((record: any, index: number) => {
        const recordTemplateData =
          record.logSheetTemplated || record.LogSheetTemplated;
        const recordDataPoints = recordTemplateData?.dataPointDetails || [];

        const nameRecord = recordDataPoints.find(
          (rdp: any) => rdp.dataPointId === namesDp.dataPointId,
        );
        const statusRecord = recordDataPoints.find(
          (rdp: any) => rdp.dataPointId === statusDp.dataPointId,
        );

        const nameValues = nameRecord?.dataPointValue || [];
        const statusValues = statusRecord?.dataPointValue || [];

        // Check if status is single-select (only one entry) vs multi-select
        const isSingleStatus = statusValues.length === 1;
        const singleStatusValue = isSingleStatus ? statusValues[0]?.value : null;

        // Iterate through all name entries
        nameValues.forEach((nameEntry: any, entryIndex: number) => {
          const rawNameValue = nameEntry?.value;

          // If status is single-select, apply it to all names
          // Otherwise, find matching status entry by logSheetEntryId
          let statusValue: string | undefined;
          if (isSingleStatus) {
            statusValue = singleStatusValue;
          } else {
            const statusEntry = statusValues.find(
              (s: any) => s.logSheetEntryId === nameEntry.logSheetEntryId,
            );
            statusValue = statusEntry?.value;
          }

          // Handle multi-select: split comma-separated values and increment each
          const nameValuesToProcess = rawNameValue?.includes(',')
            ? rawNameValue.split(',').map((n: string) => n.trim())
            : [rawNameValue];

          nameValuesToProcess.forEach((nameValue: string) => {
            if (
              nameValue &&
              statusValue &&
              crossCounts[nameValue]?.[statusValue] !== undefined
            ) {
              crossCounts[nameValue][statusValue]++;
            }
          });
        });
      });

      // Build column headers with status options
      const columnHeaders = [namesDp.dataPointName, ...statusOptions].join(",");

      // Build rows with names and their status counts
      const summaryRows = nameOptions
        .map((name: string) => {
          const counts = statusOptions.map(
            (status: string) => crossCounts[name][status] || 0,
          );
          return [this.escapeCsvValue(name), ...counts].join(",");
        })
        .join("\n");

      return `${headerWithSchedule}\n${columnHeaders}\n${summaryRows}`;
    }

    // Single list data point - list options ARE the names (rows), other data points are columns
    if (personDataPoints.length === 1) {
      const namesDp = personDataPoints[0];
      const nameOptions = namesDp.listOptions
        .split(",")
        .map((opt: string) => opt.trim());

      // Get all OTHER data points (any type) to use as column headers
      const otherDataPoints = dataPointDetails.filter(
        (dp: any) => dp.dataPointId !== namesDp.dataPointId,
      );

      if (otherDataPoints.length === 0) {
        // No other data points, fall back to simple count
        const columnHeaders = ["Name", "Count"].join(",");
        const optionCounts: { [key: string]: number } = {};
        listOptions.forEach((opt: string) => (optionCounts[opt] = 0));

        data.forEach((record: any) => {
          const recordTemplateData =
            record.logSheetTemplated || record.LogSheetTemplated;
          const recordDataPoints = recordTemplateData?.dataPointDetails || [];
          const dpRecord = recordDataPoints.find(
            (rdp: any) => rdp.dataPointId === namesDp.dataPointId,
          );
          const value = dpRecord?.dataPointValue?.[0]?.value;
          if (value && optionCounts.hasOwnProperty(value)) {
            optionCounts[value]++;
          }
        });

        const summaryRows = listOptions
          .map((option: string) =>
            [this.escapeCsvValue(option), optionCounts[option]].join(","),
          )
          .join("\n");

        return `${headerWithSchedule}\n${columnHeaders}\n${summaryRows}`;
      }

      // Check if any other data point is a list type - use its options as columns
      const listOtherDp = otherDataPoints.find(
        (dp: any) =>
          (dp.dataType === "List (Single Selection)" ||
            dp.dataType === "List (Multiple Selection)" ||
            dp.dataType === "Dropdown List") &&
          dp.listOptions,
      );

      if (listOtherDp) {
        // Use the list options from the other list data point as column headers
        const statusOptions = listOtherDp.listOptions
          .split(",")
          .map((opt: string) => opt.trim());

        // Initialize counts: names as rows, status options as columns
        const crossCounts: { [name: string]: { [status: string]: number } } =
          {};
        nameOptions.forEach((name: string) => {
          crossCounts[name] = {};
          statusOptions.forEach((status: string) => {
            crossCounts[name][status] = 0;
          });
        });

        // Count across all submissions
        data.forEach((record: any) => {
          const recordTemplateData =
            record.logSheetTemplated || record.LogSheetTemplated;
          const recordDataPoints = recordTemplateData?.dataPointDetails || [];

          // Get the name value from the names list data point
          const nameRecord = recordDataPoints.find(
            (rdp: any) => rdp.dataPointId === namesDp.dataPointId,
          );
          const nameValue = nameRecord?.dataPointValue?.[0]?.value;

          // Get the status value from the status list data point
          const statusRecord = recordDataPoints.find(
            (rdp: any) => rdp.dataPointId === listOtherDp.dataPointId,
          );
          const statusValue = statusRecord?.dataPointValue?.[0]?.value;

          if (
            nameValue &&
            statusValue &&
            crossCounts[nameValue]?.[statusValue] !== undefined
          ) {
            crossCounts[nameValue][statusValue]++;
          }
        });

        // Build column headers with status options
        const columnHeaders = ["Name", ...statusOptions].join(",");

        // Build rows with names and their status counts
        const summaryRows = nameOptions
          .map((name: string) => {
            const counts = statusOptions.map(
              (status: string) => crossCounts[name][status] || 0,
            );
            return [this.escapeCsvValue(name), ...counts].join(",");
          })
          .join("\n");

        return `${headerWithSchedule}\n${columnHeaders}\n${summaryRows}`;
      }

      // No other list data points - use other data point names as columns
      const crossCounts: { [name: string]: { [dpName: string]: any } } = {};
      nameOptions.forEach((name: string) => {
        crossCounts[name] = {};
        otherDataPoints.forEach((dp: any) => {
          crossCounts[name][dp.dataPointName] = 0;
        });
      });

      // Count across all submissions
      data.forEach((record: any) => {
        const recordTemplateData =
          record.logSheetTemplated || record.LogSheetTemplated;
        const recordDataPoints = recordTemplateData?.dataPointDetails || [];

        // Get the name value from the list data point
        const nameRecord = recordDataPoints.find(
          (rdp: any) => rdp.dataPointId === namesDp.dataPointId,
        );
        const nameValue = nameRecord?.dataPointValue?.[0]?.value;

        if (nameValue && crossCounts[nameValue]) {
          // For each other data point, get its value and increment count
          otherDataPoints.forEach((dp: any) => {
            const dpRecord = recordDataPoints.find(
              (rdp: any) => rdp.dataPointId === dp.dataPointId,
            );
            const dpValue = dpRecord?.dataPointValue?.[0]?.value;
            // If there's a truthy value, increment the count
            if (dpValue) {
              crossCounts[nameValue][dp.dataPointName]++;
            }
          });
        }
      });

      // Build column headers with other data point names
      const otherDpNames = otherDataPoints.map((dp: any) => dp.dataPointName);
      const columnHeaders = ["Name", ...otherDpNames].join(",");

      // Build rows with names and their counts for each column
      const summaryRows = nameOptions
        .map((name: string) => {
          const counts = otherDpNames.map(
            (dpName: string) => crossCounts[name][dpName] || 0,
          );
          return [this.escapeCsvValue(name), ...counts].join(",");
        })
        .join("\n");

      return `${headerWithSchedule}\n${columnHeaders}\n${summaryRows}`;
    }

    // Multiple list data points (3+) - each data point name is a team member
    const columnHeaders = ["Name", ...listOptions].join(",");

    // Build rows for ALL people (even if they have 0 counts)
    const summaryRows = personDataPoints
      .map((dp: any) => {
        const counts = listOptions.map(
          (option: string) => attendanceCounts[dp.dataPointName][option] || 0,
        );
        return [this.escapeCsvValue(dp.dataPointName), ...counts].join(",");
      })
      .join("\n");

    return `${headerWithSchedule}\n${columnHeaders}\n${summaryRows}`;
  }

  formSubmissionExtract(
    header: string,
    data: any[],
    dataPointDetails: any[],
  ): string {
    const dataPointHeaders: string[] = [];
    dataPointDetails.forEach((dp: any) => {
      dataPointHeaders.push(dp.dataPointName);
      dataPointHeaders.push(`${dp.dataPointName} (Note)`);
    });

    // Detect if this is a dynamic form by checking for multiple logSheetEntryIds
    const isDynamic = this.detectDynamicForm(data);

    const columnHeaders = [
      "Submitted By",
      ...(isDynamic ? ["Entry #"] : []),
      ...dataPointHeaders,
    ].join(",");

    const allRows: string[] = [];

    data.forEach((record: any) => {
      const submittedBy =
        record.executedByUserName ||
        record.lastModifiedByUserEmail ||
        record.currentUser ||
        "N/A";

      const recordTemplateData =
        record.logSheetTemplated || record.LogSheetTemplated;
      const recordDataPoints = recordTemplateData?.dataPointDetails || [];

      // Extract unique logSheetEntryIds from this record's data points
      const entryIds = this.extractEntryIds(recordDataPoints);

      if (isDynamic && entryIds.length > 1) {
        // Dynamic form: one row per entry/section
        entryIds.forEach((entryId: number, entryIdx: number) => {
          const dataPointValues: string[] = [];
          dataPointDetails.forEach((dp: any) => {
            const matchingDp = recordDataPoints.find(
              (rdp: any) => rdp.dataPointId === dp.dataPointId,
            );
            const values = matchingDp?.dataPointValue || [];
            const entryValue = values.find(
              (v: any) => v.logSheetEntryId === entryId,
            );
            let val = entryValue?.value || "";
            val = this.formatDataPointValue(val, dp.dataType);
            dataPointValues.push(this.escapeCsvValue(val));
            dataPointValues.push(this.escapeCsvValue(entryValue?.note || "null"));
          });

          allRows.push(
            [
              this.escapeCsvValue(submittedBy),
              entryIdx + 1,
              ...dataPointValues,
            ].join(","),
          );
        });
      } else {
        // Static form or single-entry: one row per submission
        const dataPointValues: string[] = [];
        dataPointDetails.forEach((dp: any) => {
          const matchingDp = recordDataPoints.find(
            (rdp: any) => rdp.dataPointId === dp.dataPointId,
          );
          const values = matchingDp?.dataPointValue || [];
          // For multi-select lists, join values with semicolons
          if (
            values.length > 1 &&
            !values.some((v: any) => v.logSheetEntryId !== values[0]?.logSheetEntryId)
          ) {
            dataPointValues.push(this.escapeCsvValue(
              values.map((v: any) => v.value).join("; "),
            ));
            dataPointValues.push(this.escapeCsvValue(
              values.map((v: any) => v.note || "null").join("; "),
            ));
          } else {
            let val = values[0]?.value || "";
            val = this.formatDataPointValue(val, dp.dataType);
            dataPointValues.push(this.escapeCsvValue(val));
            dataPointValues.push(this.escapeCsvValue(values[0]?.note || "null"));
          }
        });

        const row = [
          this.escapeCsvValue(submittedBy),
          ...(isDynamic ? [1] : []),
          ...dataPointValues,
        ];
        allRows.push(row.join(","));
      }
    });

    return `${header}\n${columnHeaders}\n${allRows.join("\n")}`;
  }

  /**
   * Detect if any record in the dataset has multiple logSheetEntryIds,
   * indicating a dynamic form with multiple sections per submission.
   */
  private detectDynamicForm(data: any[]): boolean {
    for (const record of data) {
      const templateData =
        record.logSheetTemplated || record.LogSheetTemplated;
      const dataPoints = templateData?.dataPointDetails || [];
      const entryIds = this.extractEntryIds(dataPoints);
      if (entryIds.length > 1) {
        return true;
      }
    }
    // Also check the formType field if available
    const firstRecord = data[0];
    const templateData =
      firstRecord?.logSheetTemplated || firstRecord?.LogSheetTemplated;
    const formType = (templateData?.formType ?? "").toString().toLowerCase().trim();
    return formType === "dynamic";
  }

  /**
   * Extract unique sorted logSheetEntryIds from a record's data point details.
   * Returns [0] as fallback if no entry IDs are found.
   */
  private extractEntryIds(dataPoints: any[]): number[] {
    const entryIdSet = new Set<number>();
    dataPoints.forEach((dp: any) => {
      const values = dp.dataPointValue || [];
      values.forEach((v: any) => {
        if (v.logSheetEntryId != null) {
          entryIdSet.add(v.logSheetEntryId);
        }
      });
    });
    const ids = Array.from(entryIdSet).sort((a, b) => a - b);
    return ids.length > 0 ? ids : [0];
  }

  /**
   * Format a date string as dd-Mmm-yyyy (e.g. 10-Mar-2026).
   * If includeTime is true, appends the time portion as h:mm AM/PM.
   * Returns the original value if it cannot be parsed as a valid date.
   */
  formatDateValue(value: string, includeTime = false): string {
    if (!value) return value;
    // Date-only strings (YYYY-MM-DD) are parsed as UTC midnight by default,
    // which shifts the date back one day in negative-offset timezones. Force local midnight instead.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T00:00:00') : new Date(value);
    if (isNaN(date.getTime())) return value;
    // If the year is unreasonable, it's not a real date
    if (date.getFullYear() < 2000 || date.getFullYear() > 2100) return value;
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let formatted = `${day}-${month}-${year}`;
    if (includeTime) {
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = hours % 12 || 12;
      formatted += ` at ${hour12}:${minutes} ${ampm}`;
    }
    return formatted;
  }

  formatDataPointValue(value: string, dataType: string): string {
    if (!value) return value;
    if (dataType === "Date") {
      return this.formatDateValue(value);
    }
    if (dataType === "Timestamp") {
      return this.formatDateValue(value, true);
    }
    return value;
  }

  escapeCsvValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    const stringValue = String(value);
    // If value contains comma, newline, or quote, wrap in quotes and escape quotes
    if (
      stringValue.includes(",") ||
      stringValue.includes("\n") ||
      stringValue.includes('"')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  // Load company (Account Holder) information for a given plant
  loadCompanyForPlant(plantId: number) {
    this.plantService.retrievePlant(plantId.toString()).subscribe({
      next: (plantDetails: any) => {
        if (
          plantDetails &&
          plantDetails.companyId &&
          plantDetails.companyName
        ) {
          // Set the company (Account Holder) information from the plant details
          this.selectedValues.company = {
            companyId: plantDetails.companyId,
            companyName: plantDetails.companyName,
            isDeleted: plantDetails.companyIsDeleted || false,
          };
          this.persistRefreshState();
        }
      },
      error: (response) => {
        console.error("Error loading company for plant:", response);
        this.errorMessage = this.errorService.handleRetrievalErrors(response);
      },
    });
  }

  private persistRefreshState(): void {
    this.refreshState.save<ReportsRefreshState>("reports", {
      companyId: this.selectedValues.company.companyId,
      plantId: this.selectedValues.plant.plantId,
      unitId: this.selectedValues.unit.unitId,
      formId: this.selectedForm.logSheetTemplateId,
      reportId: this.selectedReport.reportId,
      formatId: this.selectedFormat.formatId,
      startDate: this.startDate,
      endDate: this.endDate,
    });
  }

  private resetToMenuHead(): void {
    this.selectedValues = {
      company: { companyId: 0, companyName: "", isDeleted: false },
      plant: { plantId: 0, plantName: "", isDeleted: false },
      unit: { unitId: 0, unitName: "", isDeleted: false },
    };
    this.plants = isAdmin(this.currentRole) ? [...this.assignedPlants] : [];
    this.units = [];
    this.forms = [];
    this.selectedReport = { reportId: 0, reportName: "", isDeleted: false };
    this.selectedForm = { logSheetTemplateId: 0, logSheetName: "", isDeleted: false };
    this.selectedFormat = { formatId: 0, formatName: "", isDeleted: false };
    this.startDate = "";
    this.endDate = "";
    this.dateError = "";
    this.isGenerating = false;
    this.reportReady = false;
    this.reportData = null;
    this.resetPlant = false;
    this.resetUnit = false;
  }

  ngOnDestroy(): void {
    this.persistRefreshState();
    this.menuResetSubscription.unsubscribe();
  }
}
